import {
  createContext,
  useContext,
  useRef,
  useEffect,
  useSyncExternalStore,
  useCallback,
  type ReactNode,
} from 'react';
import {
  BridgeClient,
  createBridgeClient,
  type BridgeClientOptions,
  type ConnectionStatus,
  type SDKCaptureResult,
  type CaptureMode,
  type StreamModality,
  type VideoFrame,
  type CaptureProgress,
  type PowerComponent,
  type PowerState,
  type DeviceInfo,
} from '@ethbridge/core';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Capture state
 */
export interface CaptureState {
  /** Current capture status */
  status: 'idle' | 'capturing' | 'processing' | 'success' | 'error';

  /** Capture result data */
  data: SDKCaptureResult | null;

  /** Capture error */
  error: Error | null;

  /** Capture progress */
  progress: CaptureProgress | null;
}

/**
 * Video stream state
 */
export interface VideoState {
  /** Whether streaming is active */
  isStreaming: boolean;

  /** Current video source (data URL) */
  src: string | null;

  /** Frame count */
  frameCount: number;

  /** Last frame */
  lastFrame: VideoFrame | null;
}

/**
 * Bridge store state
 */
export interface BridgeState {
  /** Connection status */
  connectionStatus: ConnectionStatus;

  /** Whether connected */
  isConnected: boolean;

  /** Capture state */
  capture: CaptureState;

  /** Video state */
  video: VideoState;

  /** Power state */
  powerState: PowerState | null;

  /** Connected devices */
  devices: DeviceInfo[];

  /** Last error */
  lastError: Error | null;
}

/**
 * Bridge store with subscription
 */
export interface BridgeStore {
  /** Get current state */
  getState: () => BridgeState;

  /** Subscribe to state changes */
  subscribe: (listener: () => void) => () => void;

  /** Get the underlying BridgeClient */
  getClient: () => BridgeClient;
}

/**
 * Bridge context value
 */
export interface BridgeContextValue {
  /** Bridge store */
  store: BridgeStore;

  /** Connect to Iron Bridge */
  connect: () => Promise<void>;

  /** Disconnect from Iron Bridge */
  disconnect: () => Promise<void>;

  /** Reconnect to Iron Bridge */
  reconnect: () => Promise<void>;

  /** Start a capture */
  capture: (mode: CaptureMode, options?: CaptureOptions) => void;

  /** Start a capture (async) */
  captureAsync: (mode: CaptureMode, options?: CaptureOptions) => Promise<SDKCaptureResult>;

  /** Cancel current capture */
  cancelCapture: () => void;

  /** Start video stream */
  startStream: (modality: StreamModality, options?: { autoCapture?: boolean }) => void;

  /** Stop video stream */
  stopStream: () => void;

  /** Power on */
  powerOn: (component?: PowerComponent) => void;

  /** Power off */
  powerOff: (component?: PowerComponent) => void;

  /** Send command */
  sendCommand: (action: string, params?: Record<string, unknown>) => void;

  /** Send command and wait for response */
  sendCommandAsync: <T = unknown>(
    action: string,
    params?: Record<string, unknown>,
    timeoutMs?: number
  ) => Promise<T>;

  /** Refresh devices list */
  refreshDevices: () => Promise<DeviceInfo[]>;
}

/**
 * Capture options
 */
export interface CaptureOptions {
  /** Timeout in milliseconds */
  timeoutMs?: number;

  /** Minimum quality score */
  minQuality?: number;

  /** Enable video preview */
  preview?: boolean;

  /** Additional parameters */
  params?: Record<string, unknown>;
}

/**
 * BridgeProvider props
 */
export interface BridgeProviderProps {
  /** Child components */
  children: ReactNode;

  /** Bridge client options */
  options?: BridgeClientOptions;

  /** Auto-connect on mount */
  autoConnect?: boolean;

  /** Existing BridgeClient instance (for testing) */
  client?: BridgeClient;
}

// ============================================================================
// CONTEXT
// ============================================================================

const BridgeContext = createContext<BridgeContextValue | null>(null);

// ============================================================================
// STORE FACTORY
// ============================================================================

/**
 * Create a bridge store
 */
function createBridgeStore(client: BridgeClient): BridgeStore {
  let state: BridgeState = {
    connectionStatus: client.connectionStatus,
    isConnected: client.isConnected,
    capture: {
      status: 'idle',
      data: null,
      error: null,
      progress: null,
    },
    video: {
      isStreaming: client.streaming,
      src: null,
      frameCount: 0,
      lastFrame: null,
    },
    powerState: null,
    devices: [],
    lastError: null,
  };

  const listeners = new Set<() => void>();

  const notify = () => {
    listeners.forEach((listener) => listener());
  };

  const setState = (partial: Partial<BridgeState>) => {
    state = { ...state, ...partial };
    notify();
  };

  // Subscribe to client events
  client.on('connection:status', (status) => {
    setState({
      connectionStatus: status,
      isConnected: status === 'connected',
    });
  });

  client.on('capture:result', (result) => {
    setState({
      capture: {
        ...state.capture,
        status: 'success',
        data: result as SDKCaptureResult,
        error: null,
      },
    });
  });

  client.on('capture:progress', (progress) => {
    setState({
      capture: {
        ...state.capture,
        status: 'capturing',
        progress,
      },
    });
  });

  client.on('capture:status', (status) => {
    setState({
      capture: {
        ...state.capture,
        status: status === 'capturing' ? 'capturing' : status === 'processing' ? 'processing' : 'idle',
      },
    });
  });

  client.on('video:frame', (frame) => {
    const src = `data:image/${frame.format};base64,${frame.data}`;
    setState({
      video: {
        ...state.video,
        src,
        frameCount: state.video.frameCount + 1,
        lastFrame: frame,
      },
    });
  });

  client.on('video:started', () => {
    setState({
      video: {
        ...state.video,
        isStreaming: true,
        frameCount: 0,
      },
    });
  });

  client.on('video:stopped', () => {
    setState({
      video: {
        ...state.video,
        isStreaming: false,
      },
    });
  });

  client.on('power:changed', (powerState) => {
    setState({ powerState });
  });

  client.on('device:discovered', (device) => {
    setState({
      devices: [...state.devices.filter((d) => d.id !== device.id), device],
    });
  });

  client.on('device:disconnected', (deviceId) => {
    setState({
      devices: state.devices.filter((d) => d.id !== deviceId),
    });
  });

  client.on('error', (error) => {
    setState({ lastError: error });
  });

  client.on('connection:error', (error) => {
    setState({ lastError: error });
  });

  return {
    getState: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getClient: () => client,
  };
}

// ============================================================================
// PROVIDER
// ============================================================================

/**
 * BridgeProvider component
 *
 * Provides Bridge client context to child components
 *
 * @example
 * ```tsx
 * <BridgeProvider options={{ connection: { url: 'ws://localhost:4500/bridge' } }}>
 *   <App />
 * </BridgeProvider>
 * ```
 */
export function BridgeProvider({
  children,
  options,
  autoConnect = false,
  client: externalClient,
}: BridgeProviderProps): React.ReactNode {
  const clientRef = useRef<BridgeClient | null>(null);
  const storeRef = useRef<BridgeStore | null>(null);
  const cancelRef = useRef<(() => void) | null>(null);

  // Initialize client and store
  if (!clientRef.current) {
    clientRef.current = externalClient ?? createBridgeClient(options);
  }

  if (!storeRef.current) {
    storeRef.current = createBridgeStore(clientRef.current);
  }

  const client = clientRef.current;
  const store = storeRef.current;

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      client.connect().catch(console.error);
    }

    return () => {
      client.disconnect().catch(console.error);
    };
  }, [autoConnect, client]);

  // Connection methods
  const connect = useCallback(() => client.connect(), [client]);
  const disconnect = useCallback(() => client.disconnect(), [client]);
  const reconnect = useCallback(() => client.reconnect(), [client]);

  // Capture methods
  const capture = useCallback(
    (mode: CaptureMode, captureOptions?: CaptureOptions) => {
      // Cancel any pending capture
      if (cancelRef.current) {
        cancelRef.current();
      }

      const handle = client.capture(mode, captureOptions);
      cancelRef.current = handle.cancel;

      // Errors are handled via client event subscriptions in setupClientEventHandlers
      handle.promise.catch(() => {
        // Capture errors are already handled by event listeners
      });
    },
    [client, store]
  );

  const captureAsync = useCallback(
    (mode: CaptureMode, captureOptions?: CaptureOptions) => {
      return client.captureAsync(mode, captureOptions);
    },
    [client]
  );

  const cancelCapture = useCallback(() => {
    if (cancelRef.current) {
      cancelRef.current();
      cancelRef.current = null;
    }
  }, []);

  // Video methods
  const startStream = useCallback(
    (modality: StreamModality, options?: { autoCapture?: boolean }) => client.startStream(modality, options),
    [client]
  );

  const stopStream = useCallback(() => client.stopStream(), [client]);

  // Power methods
  const powerOn = useCallback(
    (component?: PowerComponent) => client.powerOn(component),
    [client]
  );

  const powerOff = useCallback(
    (component?: PowerComponent) => client.powerOff(component),
    [client]
  );

  // Command methods
  const sendCommand = useCallback(
    (action: string, params?: Record<string, unknown>) => client.sendCommand(action, params),
    [client]
  );

  const sendCommandAsync = useCallback(
    <T = unknown>(action: string, params?: Record<string, unknown>, timeoutMs?: number) =>
      client.sendCommandAsync<T>(action, params, timeoutMs),
    [client]
  );

  // Device methods
  const refreshDevices = useCallback(async () => {
    const devices = (await client.getDevices()) as DeviceInfo[];
    return devices;
  }, [client]);

  const contextValue: BridgeContextValue = {
    store,
    connect,
    disconnect,
    reconnect,
    capture,
    captureAsync,
    cancelCapture,
    startStream,
    stopStream,
    powerOn,
    powerOff,
    sendCommand,
    sendCommandAsync,
    refreshDevices,
  };

  return <BridgeContext.Provider value={contextValue}>{children}</BridgeContext.Provider>;
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Get the Bridge context
 *
 * @throws Error if used outside of BridgeProvider
 */
export function useBridgeContext(): BridgeContextValue {
  const context = useContext(BridgeContext);

  if (!context) {
    throw new Error('useBridgeContext must be used within a BridgeProvider');
  }

  return context;
}

/**
 * Subscribe to Bridge state using useSyncExternalStore
 *
 * @param selector - State selector function
 * @returns Selected state value
 */
export function useBridgeSelector<T>(selector: (state: BridgeState) => T): T {
  const { store } = useBridgeContext();

  return useSyncExternalStore(
    store.subscribe,
    () => selector(store.getState()),
    () => selector(store.getState())
  );
}
